// src/pages/admin/StripeConnectCallback.js
// OAuth callback handler for Stripe Connect
// Processes the authorization code and completes the connection

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/firebase';
import './StripeConnectCallback.css';

const StripeConnectCallback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [status, setStatus] = useState('processing'); // processing, success, error
    const [message, setMessage] = useState('Connecting your Stripe account...');
    const [error, setError] = useState('');

    useEffect(() => {
        handleOAuthCallback();
    }, []);

    const handleOAuthCallback = async () => {
        try {
            // Parse URL parameters
            const params = new URLSearchParams(location.search);
            const code = params.get('code');
            const state = params.get('state');
            const error = params.get('error');
            const errorDescription = params.get('error_description');

            // Check for OAuth errors
            if (error) {
                throw new Error(errorDescription || 'Authorization failed');
            }

            if (!code || !state) {
                throw new Error('Missing authorization code or state parameter');
            }

            setMessage('Completing Stripe connection...');

            // Call backend to complete OAuth flow
            const completeOAuth = httpsCallable(functions, 'completeStripeConnectOAuth');
            const result = await completeOAuth({ code, state });

            if (result.data.success) {
                setStatus('success');
                setMessage('✅ Stripe account connected successfully!');

                // Redirect to payment settings after 2 seconds
                setTimeout(() => {
                    navigate('/admin/settings?tab=payment', { 
                        replace: true,
                        state: { stripeConnected: true }
                    });
                }, 2000);
            } else {
                throw new Error(result.data.message || 'Failed to complete connection');
            }

        } catch (err) {
            console.error('OAuth callback error:', err);
            setStatus('error');
            setMessage('Failed to connect Stripe account');
            setError(err.message || 'An unexpected error occurred');

            // Redirect back to settings after 5 seconds
            setTimeout(() => {
                navigate('/admin/settings?tab=payment', { 
                    replace: true,
                    state: { error: err.message }
                });
            }, 5000);
        }
    };

    return (
        <div className="stripe-callback-container">
            <div className="callback-card">
                {status === 'processing' && (
                    <div className="callback-processing">
                        <div className="spinner"></div>
                        <h2>{message}</h2>
                        <p>Please wait while we securely connect your account...</p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="callback-success">
                        <div className="success-icon">✓</div>
                        <h2>{message}</h2>
                        <p>You can now accept credit card payments!</p>
                        <p className="redirect-notice">Redirecting you back to settings...</p>
                    </div>
                )}

                {status === 'error' && (
                    <div className="callback-error">
                        <div className="error-icon">✕</div>
                        <h2>{message}</h2>
                        {error && <p className="error-details">{error}</p>}
                        <p className="redirect-notice">Redirecting you back to settings...</p>
                        <button 
                            className="retry-btn"
                            onClick={() => navigate('/admin/settings?tab=payment')}
                        >
                            Go Back to Settings
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StripeConnectCallback;
